import React, { useCallback, useState } from "react";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import { FiMinus, FiPlus, FiUser } from "react-icons/fi";
import { adminOnboardUcc } from "../../../api/adminApi";
import { buildUccAddPayload } from "../../../utils/buildUccPayload";

const TEAL = "#06b6a4";

const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr", "M/s"];

const DEPOSITORY_OPTIONS = ["CDSL", "NSDL"];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
  "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir", "Ladakh",
];

const COUNTRY_OPTIONS = [
  "India", "United States", "United Kingdom", "Canada", "Australia", "Singapore", "UAE", "Other",
];

const GROSS_ANNUAL_INCOME_OPTIONS = [
  { value: "below_1_lacs", label: "Below 1 Lacs" },
  { value: "1_5_lacs", label: "1-5 Lacs" },
  { value: "5_10_lacs", label: "5-10 Lacs" },
  { value: "10_25_lacs", label: "10-25 Lacs" },
  { value: "25_lacs_1_crore", label: "25 Lacs - 1 Crore" },
  { value: "above_1_crore", label: "> 1 Crore" },
];

const INDIVIDUAL_PEP_OPTIONS = [
  { value: "pep", label: "I am Politically Exposed Person (PEP)" },
  { value: "rpep", label: "I am Related to Politically Exposed Person (RPEP)" },
  { value: "not_applicable", label: "Not Applicable" },
];

const NON_INDIVIDUAL_KYC_OPTIONS = [
  { value: "forex", label: "Foreign Exchange/Money Change Services" },
  { value: "gaming", label: "Gaming/Gambling/Lottery/Casino Services" },
  { value: "money_lending", label: "Money Lending" },
  { value: "not_applicable", label: "Not Applicable" },
];

const REQUIRED_MARK = <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>;

function FieldLabel({ children, required = false, optional = false, style }) {
  return (
    <label style={{ ...labelStyleBase, ...style }}>
      {children}
      {required && REQUIRED_MARK}
      {optional && (
        <span className="text-muted fw-normal" style={{ fontSize: "11px" }}>
          {" "}
          (optional)
        </span>
      )}
    </label>
  );
}

const labelStyleBase = {
  fontSize: "13px",
  color: "#64748b",
  marginBottom: "4px",
  display: "block",
};

function SubSectionTitle({ children }) {
  return (
    <h6
      className="mb-3 mt-2"
      style={{ fontWeight: 700, color: "#334155", fontSize: "14px" }}
    >
      {children}
    </h6>
  );
}

function BillingModeToggle({ value, onChange }) {
  const isFlat = value === "flat";
  return (
    <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
      <span style={{ fontSize: "13px", color: isFlat ? "#64748b" : "#334155", fontWeight: isFlat ? 400 : 600 }}>
        AUM Based Fee
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={isFlat}
        aria-label="Preferred billing mode"
        onClick={() => onChange(isFlat ? "aum" : "flat")}
        style={{
          width: 48,
          height: 26,
          borderRadius: 99,
          border: "none",
          background: TEAL,
          position: "relative",
          cursor: "pointer",
          padding: 0,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 3,
            left: isFlat ? 25 : 3,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#fff",
            transition: "left 0.2s ease",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        />
      </button>
      <span style={{ fontSize: "13px", color: isFlat ? "#334155" : "#64748b", fontWeight: isFlat ? 600 : 400 }}>
        Flat Fee
      </span>
    </div>
  );
}

function YesNoToggle({ value, onChange, yesLabel = "Yes", noLabel = "No" }) {
  const isYes = value === "yes";
  return (
    <div className="d-flex align-items-center gap-3 flex-wrap">
      <span style={{ fontSize: "13px", color: isYes ? "#334155" : "#64748b", fontWeight: isYes ? 600 : 400 }}>
        {yesLabel}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={isYes}
        onClick={() => onChange(isYes ? "no" : "yes")}
        style={{
          width: 48,
          height: 26,
          borderRadius: 99,
          border: "none",
          background: TEAL,
          position: "relative",
          cursor: "pointer",
          padding: 0,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 3,
            left: isYes ? 25 : 3,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#fff",
            transition: "left 0.2s ease",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        />
      </button>
      <span style={{ fontSize: "13px", color: !isYes ? "#334155" : "#64748b", fontWeight: !isYes ? 600 : 400 }}>
        {noLabel}
      </span>
    </div>
  );
}

function RadioGroup({ name, options, value, onChange, inline = false }) {
  return (
    <div className={inline ? "d-flex flex-wrap gap-3" : "d-flex flex-column gap-2"}>
      {options.map((opt) => (
        <label
          key={opt.value}
          className="d-flex align-items-center gap-2 mb-0"
          style={{ fontSize: "13px", color: "#475569", cursor: "pointer" }}
        >
          <input
            type="radio"
            name={name}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            style={{ accentColor: TEAL }}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

function AccordionSection({ title, expanded, onToggle, children }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-100 border-0 d-flex align-items-center justify-content-between"
        style={{
          padding: "14px 18px",
          background: expanded ? "#f8fafc" : "#fff",
          fontWeight: 600,
          fontSize: "15px",
          color: "#334155",
          borderBottom: expanded ? "1px solid #e2e8f0" : "none",
          cursor: "pointer",
        }}
      >
        <span>{title}</span>
        <span style={{ color: "#64748b", fontSize: "18px" }}>
          {expanded ? <FiMinus /> : <FiPlus />}
        </span>
      </button>
      {expanded && <div style={{ padding: "20px 18px" }}>{children}</div>}
    </div>
  );
}

const STEPS = [
  { id: 0, title: "Account", subtitle: "Platform login and BSE client code" },
  { id: 1, title: "Personal details", subtitle: "Holder identity and contact" },
  { id: 2, title: "Nominee & KYC", subtitle: "Nominee, KYC and FATCA information" },
  { id: 3, title: "Address", subtitle: "Communication address" },
  { id: 4, title: "Bank", subtitle: "Bank account details" },
  { id: 5, title: "Demat (CDSL)", subtitle: "Depository account" },
  { id: 6, title: "Document", subtitle: "Signed AOF (PDF)" },
];

const textOnly = /^[A-Za-z\s]+$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const ifscRegex = /^[A-Z]{4}0[0-9]{6}$/;
const digits10 = /^[0-9]{10}$/;

const INITIAL_FORM = {
  member_id: (process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim(),
  client_code: "",
  password: "",
  confirmPassword: "",
  holding_nature: "SI",
  tax_code: "01",
  rdmp_idcw_pay_mode: "02",
  comm_mode: "E",
  onboarding: "Z",
  first_name: "",
  last_name: "",
  dob: "",
  gender: "M",
  occ_code: "02",
  auth_mode: "M",
  kyc_type: "K",
  pan: "",
  phone: "",
  email: "",
  country_code: "91",
  // Personal Information (optional CRM fields)
  title: "",
  name: "",
  app_code: "",
  disable_emails: false,
  alt_email: "",
  user_name: "",
  phone_no: "",
  birthday_wish_on: "",
  anniversary: "",
  family_head: "",
  relationship_manager: "",
  sub_broker: "",
  service_rm: "",
  referred_by: "",
  // Account Details (optional CRM fields)
  equity_code_1: "",
  equity_code_2: "",
  depository_crm: "",
  dp_name: "",
  account_dp_id: "",
  nps_ac_no: "",
  annual_income: "",
  profession: "",
  liquiloans_investor_id: "",
  bill_state: "",
  bill_gstin: "",
  target_sip_amount: "",
  target_elss_amount: "",
  target_alloc_equity_pct: "",
  target_alloc_debt_pct: "",
  billing_mode: "flat",
  fee_equity_mf_pct: "0",
  fee_debt_mf_pct: "0",
  fee_shares_pct: "0",
  fee_bonds_pct: "0",
  fee_fd_pct: "0",
  fee_other_assets_pct: "0",
  account_remarks: "",
  addr1: "",
  city: "",
  state: "",
  postalcode: "",
  country: "INDIA",
  // Bank Details
  bank_name: "",
  account_holder_name: "",
  joint_holder_name: "",
  ifsc_code: "",
  bank_acc_num: "",
  bank_acc_type: "SB",
  bank_micr: "",
  bank_branch: "",
  bank_addr1: "",
  bank_addr2: "",
  bank_city: "",
  bank_pin: "",
  // Nominee Details (optional)
  nominee_name: "",
  nominee_type: "major",
  nominee_dob: "",
  nominee_share_pct: "",
  nominee_relation: "",
  nominee_addr1: "",
  nominee_addr2: "",
  // KYC Details (optional)
  gross_annual_income: "",
  net_worth: "",
  net_worth_as_on_date: "",
  individual_pep_status: "not_applicable",
  non_individual_kyc_status: "not_applicable",
  // FATCA (optional)
  fatca_nationality: "India",
  fatca_gender: "",
  place_of_birth: "",
  country_of_birth: "",
  father_name: "",
  mother_name: "",
  spouse: "",
  ckyc: "",
  tax_residency_outside_india: "no",
  dp_id: "",
  client_id: "",
  aofBase64: "",
  aofReady: false,
};

function splitFullName(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return { first_name: "", last_name: "" };
  const spaceIdx = trimmed.indexOf(" ");
  if (spaceIdx === -1) return { first_name: trimmed, last_name: "" };
  return {
    first_name: trimmed.slice(0, spaceIdx).trim(),
    last_name: trimmed.slice(spaceIdx + 1).trim(),
  };
}

function extractApiError(err) {
  const body = err?.response?.data || {};
  const nested = body.data && typeof body.data === "object" ? body.data : {};
  const missing = Array.isArray(nested.missing_fields)
    ? nested.missing_fields.join(", ")
    : "";

  const messages = body.messages || nested.messages;
  if (Array.isArray(messages) && messages.length) {
    const fromBse = messages
      .map((m) => {
        const code = m?.errcode || "error";
        const field = m?.field ? ` (${m.field})` : "";
        const vals = Array.isArray(m?.vals) ? m.vals.filter(Boolean) : [];
        const detail = vals.find((v) => String(v).length > 12) || vals[0] || "";
        return detail ? `${code}${field}: ${detail}` : `${code}${field}`;
      })
      .join("; ");
    if (fromBse) return fromBse;
  }

  const serverMsg = String(body.message || err?.message || "").trim();
  if (serverMsg) {
    return missing ? `${serverMsg} [${missing}]` : serverMsg;
  }
  if (missing) return `Missing fields: ${missing}`;
  return "Request failed";
}

export default function CreateUcc() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [personalInfoOpen, setPersonalInfoOpen] = useState(true);
  const [accountDetailsOpen, setAccountDetailsOpen] = useState(true);
  const [bankDetailsOpen, setBankDetailsOpen] = useState(true);
  const [nomineeOpen, setNomineeOpen] = useState(true);
  const [kycDetailsOpen, setKycDetailsOpen] = useState(true);
  const [fatcaOpen, setFatcaOpen] = useState(true);

  const update = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const updateName = useCallback((value) => {
    const { first_name, last_name } = splitFullName(value);
    setForm((prev) => ({ ...prev, name: value, first_name, last_name }));
  }, []);

  const validateStep = (s) => {
    switch (s) {
      case 0: {
        const mid = String(form.member_id || "").trim();
        if (!mid || !/^[0-9]+$/.test(mid)) return "Enter a valid BSE member ID.";
        const cc = form.client_code.trim();
        if (!cc || cc.length < 4) return "Client code is required (min 4 characters).";
        if (!/^[A-Za-z0-9_-]+$/.test(cc)) return "Client code: letters, numbers, hyphen, underscore only.";
        if (!form.password || form.password.length < 6) return "Password must be at least 6 characters.";
        if (form.password !== form.confirmPassword) return "Passwords do not match.";
        return null;
      }
      case 1: {
        if (!form.first_name.trim() || !textOnly.test(form.first_name.trim()))
          return "Name is required (letters only).";
        if (!form.dob) return "Date of birth is required.";
        if (!panRegex.test(form.pan.trim().toUpperCase())) return "Enter a valid PAN.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return "Enter a valid email for platform login.";
        if (!digits10.test(form.phone.replace(/\D/g, ""))) return "Enter a valid 10-digit mobile.";
        if (form.alt_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.alt_email.trim()))
          return "Alternate email is not valid.";
        return null;
      }
      case 2:
        if (form.nominee_share_pct.trim()) {
          const share = Number(form.nominee_share_pct);
          if (Number.isNaN(share) || share < 0 || share > 100) return "Nominee share must be between 0 and 100.";
        }
        return null;
      case 3: {
        if (!form.addr1.trim()) return "Address is required.";
        if (!form.city.trim()) return "City is required.";
        if (!form.state.trim()) return "State is required.";
        if (!/^[0-9]{6}$/.test(form.postalcode.trim())) return "PIN must be 6 digits.";
        return null;
      }
      case 4: {
        if (!ifscRegex.test(form.ifsc_code.trim().toUpperCase())) return "Enter a valid IFSC.";
        if (!/^[0-9]{9,18}$/.test(form.bank_acc_num.trim())) return "Bank account: 9–18 digits.";
        if (form.bank_pin.trim() && !/^[0-9]{6}$/.test(form.bank_pin.trim())) return "Bank PIN must be 6 digits.";
        return null;
      }
      case 5: {
        const dp = form.dp_id.trim();
        const cid = form.client_id.trim();
        if (dp && !/^[0-9]{8}$/.test(dp)) return "DP ID must be 8 digits when provided.";
        if (cid && !/^[0-9]{8}$/.test(cid)) return "Client ID must be 8 digits when provided.";
        if ((dp && !cid) || (!dp && cid)) return "Provide both DP ID and Client ID, or leave both empty.";
        return null;
      }
      case 6:
        if (!form.aofReady || !form.aofBase64) return "Upload the signed AOF PDF.";
        return null;
      default:
        return null;
    }
  };

  const next = () => {
    const err = validateStep(step);
    if (err) {
      Swal.fire({ icon: "warning", title: "Check this step", text: err });
      return;
    }
    setStep((x) => Math.min(STEPS.length - 1, x + 1));
  };

  const back = () => setStep((x) => Math.max(0, x - 1));

  const onPdf = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      Swal.fire({ icon: "warning", title: "Invalid file", text: "Upload a PDF file." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : "";
      if (!base64) {
        Swal.fire({ icon: "error", title: "Read failed", text: "Could not read the file." });
        return;
      }
      update({ aofBase64: base64.replace(/\s+/g, "").trim(), aofReady: true });
    };
    reader.onerror = () => Swal.fire({ icon: "error", title: "Error", text: "Failed to read file." });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const err = validateStep(6);
    if (err) {
      Swal.fire({ icon: "warning", title: "Almost there", text: err });
      return;
    }

    setSubmitting(true);
    try {
      const bse = buildUccAddPayload(form);
      const res = await adminOnboardUcc({
        user: {
          email: form.email.trim(),
          password: form.password,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          pan_number: form.pan.trim().toUpperCase(),
          phone: form.phone.replace(/\D/g, "").slice(-10),
          country_code: form.country_code.replace(/\D/g, "") || "91",
          agent_id: form.relationship_manager || undefined,
          profile: {
            title: form.title.trim() || undefined,
            app_code: form.app_code.trim() || undefined,
            disable_emails: form.disable_emails || undefined,
            alt_email: form.alt_email.trim() || undefined,
            user_name: form.user_name.trim() || undefined,
            phone_no: form.phone_no.trim() || undefined,
            birthday_wish_on: form.birthday_wish_on.trim() || undefined,
            anniversary: form.anniversary.trim() || undefined,
            family_head: form.family_head || undefined,
            relationship_manager: form.relationship_manager || undefined,
            sub_broker: form.sub_broker || undefined,
            service_rm: form.service_rm || undefined,
            referred_by: form.referred_by || undefined,
            account_details: {
              equity_code_1: form.equity_code_1.trim() || undefined,
              equity_code_2: form.equity_code_2.trim() || undefined,
              depository: form.depository_crm || undefined,
              dp_name: form.dp_name.trim() || undefined,
              dp_id: form.account_dp_id.trim() || undefined,
              nps_ac_no: form.nps_ac_no.trim() || undefined,
              annual_income: form.annual_income.trim() || undefined,
              profession: form.profession.trim() || undefined,
              liquiloans_investor_id: form.liquiloans_investor_id.trim() || undefined,
              bill_state: form.bill_state || undefined,
              bill_gstin: form.bill_gstin.trim() || undefined,
              target_sip_amount: form.target_sip_amount.trim() || undefined,
              target_elss_amount: form.target_elss_amount.trim() || undefined,
              target_allocation: {
                equity_pct: form.target_alloc_equity_pct.trim() || undefined,
                debt_pct: form.target_alloc_debt_pct.trim() || undefined,
              },
              billing_mode: form.billing_mode || undefined,
              flat_fee_allocation: {
                equity_mf_pct: form.fee_equity_mf_pct.trim() || undefined,
                debt_mf_pct: form.fee_debt_mf_pct.trim() || undefined,
                shares_pct: form.fee_shares_pct.trim() || undefined,
                bonds_pct: form.fee_bonds_pct.trim() || undefined,
                fixed_deposit_pct: form.fee_fd_pct.trim() || undefined,
                other_assets_pct: form.fee_other_assets_pct.trim() || undefined,
              },
              remarks: form.account_remarks.trim() || undefined,
            },
            bank_details: {
              bank_name: form.bank_name.trim() || undefined,
              account_holder_name: form.account_holder_name.trim() || undefined,
              joint_holder_name: form.joint_holder_name.trim() || undefined,
              account_no: form.bank_acc_num.trim() || undefined,
              account_type: form.bank_acc_type || undefined,
              ifsc: form.ifsc_code.trim().toUpperCase() || undefined,
              micr: form.bank_micr.trim() || undefined,
              branch: form.bank_branch.trim() || undefined,
              address_line_1: form.bank_addr1.trim() || undefined,
              address_line_2: form.bank_addr2.trim() || undefined,
              city: form.bank_city.trim() || undefined,
              pin: form.bank_pin.trim() || undefined,
            },
            nominee_details: {
              name: form.nominee_name.trim() || undefined,
              type: form.nominee_type || undefined,
              dob: form.nominee_dob.trim() || undefined,
              share_pct: form.nominee_share_pct.trim() || undefined,
              relation: form.nominee_relation.trim() || undefined,
              address_line_1: form.nominee_addr1.trim() || undefined,
              address_line_2: form.nominee_addr2.trim() || undefined,
            },
            kyc_details: {
              gross_annual_income: form.gross_annual_income || undefined,
              net_worth: form.net_worth.trim() || undefined,
              net_worth_as_on_date: form.net_worth_as_on_date.trim() || undefined,
              individual_pep_status: form.individual_pep_status || undefined,
              non_individual_status: form.non_individual_kyc_status || undefined,
            },
            fatca: {
              nationality: form.fatca_nationality || undefined,
              gender: form.fatca_gender || undefined,
              place_of_birth: form.place_of_birth.trim() || undefined,
              country_of_birth: form.country_of_birth || undefined,
              father_name: form.father_name.trim() || undefined,
              mother_name: form.mother_name.trim() || undefined,
              spouse: form.spouse.trim() || undefined,
              ckyc: form.ckyc.trim() || undefined,
              tax_residency_outside_india: form.tax_residency_outside_india === "yes",
            },
            residential_address: {
              address_line_1: form.addr1.trim() || undefined,
              city: form.city.trim() || undefined,
              state: form.state.trim() || undefined,
              postalcode: form.postalcode.trim() || undefined,
              country: form.country.trim() || undefined,
            },
            demat: {
              dp_id: form.dp_id.trim() || undefined,
              client_id: form.client_id.trim() || undefined,
              depository_code: "CDSL",
            },
          },
        },
        bse,
      });

      const data = res?.data?.data || {};
      await Swal.fire({
        icon: "success",
        title: "UCC & user created",
        html: `
          <div class="text-start">
            <p><strong>Platform user ID:</strong> ${data.user_id}</p>
            <p><strong>BSE client code:</strong> ${data.client_code}</p>
            <p><strong>Email:</strong> ${form.email}</p>
            <p class="small text-muted mb-0">User can log in with the email and password you set. UCC and profile data saved on GPS Finvest.</p>
          </div>
        `,
      });
      setForm(INITIAL_FORM);
      setStep(0);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Onboard failed",
        html: extractApiError(error),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = ((step + 1) / STEPS.length) * 100;
  const inputStyle = {
    borderRadius: "6px",
    border: "1px solid #cbd5e1",
    padding: "8px 12px",
    fontSize: "14px",
    width: "100%",
    backgroundColor: "#fff",
    color: "#334155",
  };

  return (
    <>
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
            <div className="card-body p-4">
              <nav aria-label="breadcrumb" className="mb-2">
                <ol className="breadcrumb mb-0 small">
                  <li className="breadcrumb-item">
                    <NavLink to="/admin">Admin</NavLink>
                  </li>
                  <li className="breadcrumb-item">
                    <NavLink to="/admin/invest-bse/ucc-list">Invest Online BSE</NavLink>
                  </li>
                  <li className="breadcrumb-item active">Create UCC</li>
                </ol>
              </nav>
              <h3 className="mb-1" style={{ color: "#0f172a" }}>
                Create UCC &amp; Platform User
              </h3>
              <p className="text-muted mb-0">
                Register investor on BSE Star MF and create a client login on GPS Finvest in one step.
              </p>
            </div>
          </div>

          <div className="card shadow-sm border-0 mb-3" style={{ borderRadius: "12px" }}>
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-muted">
                  Step {step + 1} of {STEPS.length}: <strong>{STEPS[step].title}</strong>
                </span>
              </div>
              <div style={{ height: 8, background: "#e2e8f0", borderRadius: 99, overflow: "hidden" }}>
                <div
                  style={{
                    width: `${progressPct}%`,
                    height: "100%",
                    background: `linear-gradient(90deg, ${TEAL}, #0891b2)`,
                    transition: "width 0.25s ease",
                  }}
                />
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0" style={{ borderRadius: "12px", maxWidth: 1100, margin: "0 auto" }}>
            <div className="card-body p-4">
              <p className="text-muted small mb-4">{STEPS[step].subtitle}</p>

              {step === 0 && (
                <div className="row g-3">
                  <div className="col-md-6">
                    <FieldLabel required>BSE member ID</FieldLabel>
                    <input className="form-control" style={inputStyle} value={form.member_id} onChange={(e) => update({ member_id: e.target.value.replace(/\D/g, "") })} />
                  </div>
                  <div className="col-md-6">
                    <FieldLabel required>BSE client code (UCC)</FieldLabel>
                    <input className="form-control" style={inputStyle} value={form.client_code} onChange={(e) => update({ client_code: e.target.value })} placeholder="Unique code on BSE" />
                  </div>
                  <div className="col-md-6">
                    <FieldLabel required>Password</FieldLabel>
                    <input type="password" className="form-control" style={inputStyle} value={form.password} onChange={(e) => update({ password: e.target.value })} />
                  </div>
                  <div className="col-md-6">
                    <FieldLabel required>Confirm password</FieldLabel>
                    <input type="password" className="form-control" style={inputStyle} value={form.confirmPassword} onChange={(e) => update({ confirmPassword: e.target.value })} />
                  </div>
                </div>
              )}

              {step === 1 && (
                <>
                <AccordionSection
                  title="Personal Information"
                  expanded={personalInfoOpen}
                  onToggle={() => setPersonalInfoOpen((v) => !v)}
                >
                  <div className="row g-3">
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Title</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.title} onChange={(e) => update({ title: e.target.value })}>
                        <option value="">Select</option>
                        {TITLE_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-6">
                      <FieldLabel required>Name</FieldLabel>
                      <input
                        className="form-control"
                        style={inputStyle}
                        value={form.name || `${form.first_name}${form.last_name ? ` ${form.last_name}` : ""}`.trim()}
                        onChange={(e) => updateName(e.target.value)}
                        placeholder="Full name"
                      />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel required>PAN</FieldLabel>
                      <input
                        className="form-control text-uppercase"
                        style={inputStyle}
                        value={form.pan}
                        onChange={(e) => update({ pan: e.target.value.toUpperCase() })}
                        maxLength={10}
                      />
                    </div>

                    <div className="col-6 col-md-3">
                      <FieldLabel optional>App code</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.app_code} onChange={(e) => update({ app_code: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-5">
                      <FieldLabel required>Email ID</FieldLabel>
                      <input type="email" className="form-control" style={inputStyle} value={form.email} onChange={(e) => update({ email: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-4 d-flex align-items-end pb-1">
                      <label className="d-flex align-items-center gap-2 mb-0" style={{ fontSize: "13px", color: "#475569", cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={form.disable_emails}
                          onChange={(e) => update({ disable_emails: e.target.checked })}
                          style={{ accentColor: TEAL }}
                        />
                        Disable Emails
                      </label>
                    </div>
                    <div className="col-12 col-md-3">
                      <FieldLabel optional>Alternate Email ID</FieldLabel>
                      <input type="email" className="form-control" style={inputStyle} value={form.alt_email} onChange={(e) => update({ alt_email: e.target.value })} />
                    </div>

                    <div className="col-6 col-md-3">
                      <FieldLabel optional>User Name</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.user_name} onChange={(e) => update({ user_name: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel required>Mobile No</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.phone} onChange={(e) => update({ phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} maxLength={10} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Phone No</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.phone_no} onChange={(e) => update({ phone_no: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel required>Gender</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.gender} onChange={(e) => update({ gender: e.target.value })}>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="O">Other</option>
                      </select>
                    </div>

                    <div className="col-6 col-md-3">
                      <FieldLabel required>Date of Birth</FieldLabel>
                      <input type="date" className="form-control" style={inputStyle} value={form.dob} onChange={(e) => update({ dob: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Birthday wish on</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.birthday_wish_on} onChange={(e) => update({ birthday_wish_on: e.target.value })} placeholder="DD-MM-YYYY" />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Anniversary</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.anniversary} onChange={(e) => update({ anniversary: e.target.value })} placeholder="DD-MM-YYYY" />
                    </div>
                    <div className="col-6 col-md-3" />

                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Family Head</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.family_head} onChange={(e) => update({ family_head: e.target.value })}>
                        <option value="">Select</option>
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Relationship Manager</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.relationship_manager} onChange={(e) => update({ relationship_manager: e.target.value })}>
                        <option value="">Select</option>
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Sub Broker</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.sub_broker} onChange={(e) => update({ sub_broker: e.target.value })}>
                        <option value="">Select</option>
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Service RM</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.service_rm} onChange={(e) => update({ service_rm: e.target.value })}>
                        <option value="">Select</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Referred By</FieldLabel>
                      <div
                        className="d-inline-flex align-items-center gap-2 w-100"
                        style={{ maxWidth: 320 }}
                      >
                        <FiUser style={{ color: TEAL, flexShrink: 0 }} />
                        <select
                          className="form-select flex-grow-1"
                          style={{ ...inputStyle, borderColor: TEAL }}
                          value={form.referred_by}
                          onChange={(e) => update({ referred_by: e.target.value })}
                        >
                          <option value="">Select User</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </AccordionSection>

                <AccordionSection
                  title="Account Details"
                  expanded={accountDetailsOpen}
                  onToggle={() => setAccountDetailsOpen((v) => !v)}
                >
                  <div className="row g-3">
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Equity Code 1</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.equity_code_1} onChange={(e) => update({ equity_code_1: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Equity Code 2</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.equity_code_2} onChange={(e) => update({ equity_code_2: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Depository</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.depository_crm} onChange={(e) => update({ depository_crm: e.target.value })}>
                        <option value="">Select</option>
                        {DEPOSITORY_OPTIONS.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>DP Name</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.dp_name} onChange={(e) => update({ dp_name: e.target.value })} />
                    </div>

                    <div className="col-6 col-md-3">
                      <FieldLabel optional>DP ID</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.account_dp_id} onChange={(e) => update({ account_dp_id: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>NPS A/c No</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.nps_ac_no} onChange={(e) => update({ nps_ac_no: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Annual Income</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.annual_income} onChange={(e) => update({ annual_income: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Profession</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.profession} onChange={(e) => update({ profession: e.target.value })} />
                    </div>

                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Liquiloans Investor ID</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.liquiloans_investor_id} onChange={(e) => update({ liquiloans_investor_id: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Bill State</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.bill_state} onChange={(e) => update({ bill_state: e.target.value })}>
                        <option value="">Select</option>
                        {INDIAN_STATES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Bill GSTIN</FieldLabel>
                      <input className="form-control text-uppercase" style={inputStyle} value={form.bill_gstin} onChange={(e) => update({ bill_gstin: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Target SIP Amount</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.target_sip_amount} onChange={(e) => update({ target_sip_amount: e.target.value })} />
                    </div>

                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Target ELSS Amount</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.target_elss_amount} onChange={(e) => update({ target_elss_amount: e.target.value })} />
                    </div>
                  </div>

                  <SubSectionTitle>Target Allocation</SubSectionTitle>
                  <div className="row g-3 mb-2">
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Equity (%)</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.target_alloc_equity_pct} onChange={(e) => update({ target_alloc_equity_pct: e.target.value.replace(/[^\d.]/g, "") })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Debt (%)</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.target_alloc_debt_pct} onChange={(e) => update({ target_alloc_debt_pct: e.target.value.replace(/[^\d.]/g, "") })} />
                    </div>
                  </div>

                  <SubSectionTitle>Preferred Billing mode</SubSectionTitle>
                  <BillingModeToggle value={form.billing_mode} onChange={(v) => update({ billing_mode: v })} />
                  <div className="row g-3 mb-2">
                    {[
                      { key: "fee_equity_mf_pct", label: "Equity MF (%)" },
                      { key: "fee_debt_mf_pct", label: "Debt MF (%)" },
                      { key: "fee_shares_pct", label: "Shares (%)" },
                      { key: "fee_bonds_pct", label: "Bonds (%)" },
                      { key: "fee_fd_pct", label: "Fixed Deposit (%)" },
                      { key: "fee_other_assets_pct", label: "Other Assets (%)" },
                    ].map(({ key, label }) => (
                      <div key={key} className="col-6 col-md-2">
                        <FieldLabel optional>{label}</FieldLabel>
                        <input
                          className="form-control"
                          style={inputStyle}
                          value={form[key]}
                          onChange={(e) => update({ [key]: e.target.value.replace(/[^\d.]/g, "") })}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-3">
                    <FieldLabel optional>Remarks</FieldLabel>
                    <textarea
                      className="form-control"
                      style={{ ...inputStyle, minHeight: 90, resize: "vertical" }}
                      value={form.account_remarks}
                      onChange={(e) => update({ account_remarks: e.target.value })}
                      rows={3}
                    />
                  </div>
                </AccordionSection>
                </>
              )}

              {step === 2 && (
                <>
                <AccordionSection
                  title="Nominee Details"
                  expanded={nomineeOpen}
                  onToggle={() => setNomineeOpen((v) => !v)}
                >
                  <div className="row g-3">
                    <div className="col-12 col-md-8">
                      <FieldLabel optional>Nominee Name</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.nominee_name} onChange={(e) => update({ nominee_name: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-4">
                      <FieldLabel optional>Nominee Type</FieldLabel>
                      <RadioGroup
                        name="nominee_type"
                        inline
                        value={form.nominee_type}
                        onChange={(v) => update({ nominee_type: v })}
                        options={[
                          { value: "major", label: "Major" },
                          { value: "minor", label: "Minor" },
                        ]}
                      />
                    </div>

                    <div className="col-6 col-md-4">
                      <FieldLabel optional>Date of Birth</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.nominee_dob} onChange={(e) => update({ nominee_dob: e.target.value })} placeholder="DD-MM-YYYY" />
                    </div>
                    <div className="col-6 col-md-4">
                      <FieldLabel optional>Share (%)</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.nominee_share_pct} onChange={(e) => update({ nominee_share_pct: e.target.value.replace(/[^\d.]/g, "") })} />
                    </div>
                    <div className="col-12 col-md-4">
                      <FieldLabel optional>Relation</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.nominee_relation} onChange={(e) => update({ nominee_relation: e.target.value })} />
                    </div>

                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Address Line 1</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.nominee_addr1} onChange={(e) => update({ nominee_addr1: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Address Line 2</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.nominee_addr2} onChange={(e) => update({ nominee_addr2: e.target.value })} />
                    </div>
                  </div>
                </AccordionSection>

                <AccordionSection
                  title="KYC Details"
                  expanded={kycDetailsOpen}
                  onToggle={() => setKycDetailsOpen((v) => !v)}
                >
                  <div className="mb-4">
                    <FieldLabel optional>Gross Annual Income</FieldLabel>
                    <RadioGroup
                      name="gross_annual_income"
                      inline
                      value={form.gross_annual_income}
                      onChange={(v) => update({ gross_annual_income: v })}
                      options={GROSS_ANNUAL_INCOME_OPTIONS}
                    />
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Net Worth (Rs)</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.net_worth} onChange={(e) => update({ net_worth: e.target.value.replace(/[^\d.]/g, "") })} />
                    </div>
                    <div className="col-12 col-md-6">
                      <FieldLabel optional>As on Date</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.net_worth_as_on_date} onChange={(e) => update({ net_worth_as_on_date: e.target.value })} placeholder="DD-MM-YYYY" />
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <SubSectionTitle>For Individuals</SubSectionTitle>
                      <RadioGroup
                        name="individual_pep_status"
                        value={form.individual_pep_status}
                        onChange={(v) => update({ individual_pep_status: v })}
                        options={INDIVIDUAL_PEP_OPTIONS}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <SubSectionTitle>For Non-Individuals</SubSectionTitle>
                      <RadioGroup
                        name="non_individual_kyc_status"
                        value={form.non_individual_kyc_status}
                        onChange={(v) => update({ non_individual_kyc_status: v })}
                        options={NON_INDIVIDUAL_KYC_OPTIONS}
                      />
                    </div>
                  </div>
                </AccordionSection>

                <AccordionSection
                  title="FATCA"
                  expanded={fatcaOpen}
                  onToggle={() => setFatcaOpen((v) => !v)}
                >
                  <div className="row g-3">
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Nationality</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.fatca_nationality} onChange={(e) => update({ fatca_nationality: e.target.value })}>
                        {COUNTRY_OPTIONS.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Gender</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.fatca_gender} onChange={(e) => update({ fatca_gender: e.target.value })}>
                        <option value="">Select</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="O">Other</option>
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Place of Birth</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.place_of_birth} onChange={(e) => update({ place_of_birth: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>Country of Birth</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.country_of_birth} onChange={(e) => update({ country_of_birth: e.target.value })}>
                        <option value="">Select</option>
                        {COUNTRY_OPTIONS.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Father&apos;s Name</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.father_name} onChange={(e) => update({ father_name: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Mother&apos;s Name</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.mother_name} onChange={(e) => update({ mother_name: e.target.value })} />
                    </div>

                    <div className="col-12 col-md-8">
                      <FieldLabel optional>Spouse</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.spouse} onChange={(e) => update({ spouse: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-4">
                      <FieldLabel optional>CKYC</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.ckyc} onChange={(e) => update({ ckyc: e.target.value })} />
                    </div>
                  </div>

                  <div className="mt-4 pt-3" style={{ borderTop: "1px solid #e2e8f0" }}>
                    <p className="mb-3" style={{ fontSize: "13px", color: "#475569", lineHeight: 1.5 }}>
                      If the applicant(s) / Guardian&apos;s Country of Birth / Citizenship / Nationality / Tax Residency
                      is other than Indian, please indicate all countries in which you are resident for tax purposes
                      and the associated TAX ID Number below.
                    </p>
                    <YesNoToggle
                      value={form.tax_residency_outside_india}
                      onChange={(v) => update({ tax_residency_outside_india: v })}
                    />
                  </div>
                </AccordionSection>
                </>
              )}

              {step === 3 && (
                <div className="row g-3">
                  <div className="col-12">
                    <FieldLabel required>Address line 1</FieldLabel>
                    <input className="form-control" style={inputStyle} value={form.addr1} onChange={(e) => update({ addr1: e.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <FieldLabel required>City</FieldLabel>
                    <input className="form-control" style={inputStyle} value={form.city} onChange={(e) => update({ city: e.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <FieldLabel required>State</FieldLabel>
                    <input className="form-control" style={inputStyle} value={form.state} onChange={(e) => update({ state: e.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <FieldLabel required>PIN code</FieldLabel>
                    <input className="form-control" style={inputStyle} value={form.postalcode} onChange={(e) => update({ postalcode: e.target.value.replace(/\D/g, "").slice(0, 6) })} />
                  </div>
                </div>
              )}

              {step === 4 && (
                <AccordionSection
                  title="Bank Details"
                  expanded={bankDetailsOpen}
                  onToggle={() => setBankDetailsOpen((v) => !v)}
                >
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Bank Name</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.bank_name} onChange={(e) => update({ bank_name: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-6">
                      <FieldLabel optional>A/c holder name</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.account_holder_name} onChange={(e) => update({ account_holder_name: e.target.value })} />
                    </div>

                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Joint holder name</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.joint_holder_name} onChange={(e) => update({ joint_holder_name: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel required>A/c No.</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.bank_acc_num} onChange={(e) => update({ bank_acc_num: e.target.value.replace(/\D/g, "") })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel required>Account Type</FieldLabel>
                      <select className="form-select" style={inputStyle} value={form.bank_acc_type} onChange={(e) => update({ bank_acc_type: e.target.value })}>
                        <option value="SB">Savings</option>
                        <option value="CB">Current</option>
                      </select>
                    </div>

                    <div className="col-6 col-md-3">
                      <FieldLabel required>IFSC</FieldLabel>
                      <input className="form-control text-uppercase" style={inputStyle} value={form.ifsc_code} onChange={(e) => update({ ifsc_code: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>MICR</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.bank_micr} onChange={(e) => update({ bank_micr: e.target.value.replace(/\D/g, "") })} />
                    </div>
                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Branch</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.bank_branch} onChange={(e) => update({ bank_branch: e.target.value })} />
                    </div>

                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Address</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.bank_addr1} onChange={(e) => update({ bank_addr1: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-6">
                      <FieldLabel optional>Address line 2</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.bank_addr2} onChange={(e) => update({ bank_addr2: e.target.value })} />
                    </div>

                    <div className="col-6 col-md-3">
                      <FieldLabel optional>City</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.bank_city} onChange={(e) => update({ bank_city: e.target.value })} />
                    </div>
                    <div className="col-6 col-md-3">
                      <FieldLabel optional>PIN</FieldLabel>
                      <input className="form-control" style={inputStyle} value={form.bank_pin} onChange={(e) => update({ bank_pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} maxLength={6} />
                    </div>
                  </div>
                </AccordionSection>
              )}

              {step === 5 && (
                <div className="row g-3">
                  <div className="col-md-6">
                    <FieldLabel optional>DP ID (CDSL)</FieldLabel>
                    <input className="form-control" style={inputStyle} value={form.dp_id} onChange={(e) => update({ dp_id: e.target.value.replace(/\D/g, "").slice(0, 8) })} />
                  </div>
                  <div className="col-md-6">
                    <FieldLabel optional>Client ID (CDSL)</FieldLabel>
                    <input className="form-control" style={inputStyle} value={form.client_id} onChange={(e) => update({ client_id: e.target.value.replace(/\D/g, "").slice(0, 8) })} />
                  </div>
                </div>
              )}

              {step === 6 && (
                <div>
                  <FieldLabel required>Upload AOF PDF</FieldLabel>
                  <input type="file" className="form-control" accept="application/pdf" onChange={onPdf} />
                  {form.aofReady && <div className="form-text text-success mt-2">PDF ready for submission.</div>}
                </div>
              )}

              <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                <button type="button" className="btn btn-outline-secondary" onClick={back} disabled={step === 0 || submitting}>
                  Back
                </button>
                {step < STEPS.length - 1 ? (
                  <button type="button" className="btn text-white" style={{ background: TEAL }} onClick={next}>
                    Next
                  </button>
                ) : (
                  <button type="button" className="btn text-white" style={{ background: TEAL }} onClick={submit} disabled={submitting}>
                    {submitting ? "Creating..." : "Create UCC & User"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
